import realRatingData from '../data/realRatingData.json';
import { COMPONENT_WEIGHTS, COMPONENT_INFO, RATING_SCALE } from '../constants/camelRatings';

// Type definitions
interface InstitutionData {
  institution_name: string;
  rating: string;
  date: string;
  finalScore: number;
  camel: Array<{ code: string; score: number }>;
  sankeyIndicators: Array<{
    indicator_id: string;
    indicator_name: string;
    indicator_value: string;
    indicator_score: string;
    indicator_category: string;
  }>;
  history?: any[];
}

// Tool definitions for Claude
export const AGENT_TOOLS = [
  {
    name: 'get_institution_data',
    description: 'Get complete rating data for a financial institution including current rating, scores, all CAMEL components, and indicators. Use this when the user asks about a specific institution\'s rating or wants to understand why it has a certain rating.',
    input_schema: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID to search for (e.g., "COOPROGRESO", "JARDIN AZUAYO", "ANDALUCIA", or institution ID like "COOP_1790451801001"). Partial matches work.',
        },
        period: {
          type: 'string',
          description: 'Optional period in YYYY-MM format (e.g., "2025-05", "2024-12"). If not provided, uses latest period.',
        },
      },
      required: ['institution_query'],
    },
  },
  {
    name: 'compare_institutions',
    description: 'Compare two financial institutions side-by-side, showing differences in ratings, scores, and all CAMEL components. Use this when the user asks to compare two institutions or wants to know how one institution stacks up against another.',
    input_schema: {
      type: 'object',
      properties: {
        institution1_query: {
          type: 'string',
          description: 'First institution name or partial name (e.g., "JARDIN", "COOPROGRESO")',
        },
        institution2_query: {
          type: 'string',
          description: 'Second institution name or partial name',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest). Both institutions will be compared at this same period.',
        },
      },
      required: ['institution1_query', 'institution2_query'],
    },
  },
  {
    name: 'get_rating_history',
    description: 'Get historical rating trend for an institution over time, showing how the rating and score have changed month by month. Use this when the user asks about trends, changes over time, or how an institution has performed historically.',
    input_schema: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        months: {
          type: 'number',
          description: 'Number of months to look back (default: 6, max: 13)',
        },
      },
      required: ['institution_query'],
    },
  },
  {
    name: 'get_component_analysis',
    description: 'Get detailed analysis of a specific CAMEL component including all its indicators, their scores, and weights. Use this when the user asks about a specific component (Capital, Liquidity, Profitability, etc.) or wants to understand what\'s driving a component\'s score.',
    input_schema: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        component_code: {
          type: 'string',
          enum: ['C', 'EG', 'L', 'R', 'S', 'STI'],
          description: 'CAMEL component code: C (Capital/Asset Quality), EG (Efficiency & Earnings), L (Liquidity), R (Profitability), S (Solvency/Asset Quality), STI (Interest Rate Sensitivity)',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest)',
        },
      },
      required: ['institution_query', 'component_code'],
    },
  },
  {
    name: 'get_methodology_info',
    description: 'Get information about CAMEL rating methodology, including component weights, calculation formulas, and rating scale. Use this when the user asks how ratings are calculated, what the methodology is, or wants to understand the scoring system.',
    input_schema: {
      type: 'object',
      properties: {
        detail_level: {
          type: 'string',
          enum: ['overview', 'detailed', 'formulas'],
          description: 'Level of detail: overview (basic explanation), detailed (with weights), formulas (including interpolation formulas and examples)',
        },
      },
      required: [],
    },
  },
  {
    name: 'get_weakest_strongest_indicators',
    description: 'Find the weakest or strongest performing indicators for an institution. Use this when the user asks what indicators are hurting/helping the score, what\'s dragging down/boosting the rating, or wants to see problem areas or strengths.',
    input_schema: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        order: {
          type: 'string',
          enum: ['weakest', 'strongest'],
          description: 'Get weakest (lowest scoring) or strongest (highest scoring) indicators',
        },
        limit: {
          type: 'number',
          description: 'Number of indicators to return (default: 5)',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest)',
        },
      },
      required: ['institution_query', 'order'],
    },
  },
  {
    name: 'get_indicator_history',
    description: 'Get historical trend data for a specific financial indicator over multiple periods. Use this when the user asks about trends for a specific indicator (like "Morosidad", "NPL ratio", "ROA", etc.), wants to see how an indicator has changed over time, or asks for a historical graph/chart of an indicator.',
    input_schema: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        indicator_id: {
          type: 'string',
          description: 'Indicator ID (e.g., "C2" for Non-Performing Loans, "R1" for ROA, "L1" for Liquidity Ratio). You can also use the full indicator name and the system will try to match it.',
        },
        months: {
          type: 'number',
          description: 'Number of months to look back (default: 13, max: 13)',
        },
      },
      required: ['institution_query', 'indicator_id'],
    },
  },
];

// Helper function to find institution ID from query (fuzzy matching)
function findInstitutionId(query: string): string | null {
  const lowerQuery = query.toLowerCase().trim();
  const data = realRatingData as Record<string, InstitutionData>;

  // First try exact ID match
  if (data[query]) {
    return query;
  }

  // Then try case-insensitive ID match
  for (const id of Object.keys(data)) {
    if (id.toLowerCase() === lowerQuery) {
      return id;
    }
  }

  // Finally try fuzzy name matching
  for (const [id, instData] of Object.entries(data)) {
    const name = instData.institution_name.toLowerCase();
    if (name.includes(lowerQuery) || lowerQuery.includes(name.split(' ')[0])) {
      return id;
    }
  }

  return null;
}

// Helper to determine which component an indicator belongs to
function getComponentForIndicator(indicatorId: string): string {
  // Most indicators start with component code
  const prefix = indicatorId.substring(0, 1);
  if (['C', 'L', 'R', 'S'].includes(prefix)) return prefix;

  // EG indicators
  if (indicatorId.startsWith('EG')) return 'EG';

  // STI indicators
  if (indicatorId.startsWith('STI')) return 'STI';

  return prefix;
}

// Tool implementation functions
export function getInstitutionData(params: { institution_query: string; period?: string }) {
  const { institution_query, period } = params;
  const data = realRatingData as Record<string, InstitutionData>;

  // Find institution
  const institutionId = findInstitutionId(institution_query);
  if (!institutionId) {
    return {
      error: `Institution "${institution_query}" not found. Available institutions: JARDIN AZUAYO, ANDALUCIA, COOPROGRESO`,
      available_institutions: Object.values(data).map(d => d.institution_name)
    };
  }

  const instData = data[institutionId];

  // If specific period requested, get from history
  if (period && period !== instData.date) {
    const historicalData = instData.history?.find((h: any) => h.date === period);
    if (!historicalData) {
      return {
        error: `Period ${period} not found for this institution`,
        available_periods: [instData.date, ...(instData.history?.map((h: any) => h.date) || [])]
      };
    }
    return {
      institution_id: institutionId,
      institution_name: instData.institution_name,
      ...historicalData,
    };
  }

  // Return current data
  return {
    institution_id: institutionId,
    ...instData,
  };
}

export function compareInstitutions(params: {
  institution1_query: string;
  institution2_query: string;
  period?: string;
}) {
  const inst1 = getInstitutionData({
    institution_query: params.institution1_query,
    period: params.period
  });
  const inst2 = getInstitutionData({
    institution_query: params.institution2_query,
    period: params.period
  });

  if ('error' in inst1 || 'error' in inst2) {
    return {
      error: 'One or both institutions not found',
      inst1_error: 'error' in inst1 ? inst1.error : null,
      inst2_error: 'error' in inst2 ? inst2.error : null,
    };
  }

  // Calculate differences
  const scoreDiff = inst1.finalScore - inst2.finalScore;
  const componentDiffs = inst1.camel.map((c1: any, idx: number) => ({
    component: c1.code,
    component_name: COMPONENT_INFO[c1.code as keyof typeof COMPONENT_INFO].name,
    inst1_score: c1.score,
    inst2_score: inst2.camel[idx].score,
    difference: c1.score - inst2.camel[idx].score,
    weight: COMPONENT_WEIGHTS[c1.code as keyof typeof COMPONENT_WEIGHTS],
  }));

  return {
    institution1: inst1,
    institution2: inst2,
    comparison: {
      score_difference: scoreDiff,
      rating_difference: `${inst1.rating} vs ${inst2.rating}`,
      component_differences: componentDiffs,
    },
  };
}

export function getRatingHistory(params: { institution_query: string; months?: number }) {
  const { institution_query, months = 6 } = params;
  const data = realRatingData as Record<string, InstitutionData>;

  const institutionId = findInstitutionId(institution_query);
  if (!institutionId) {
    return { error: `Institution "${institution_query}" not found` };
  }

  const instData = data[institutionId];
  const history = instData.history?.slice(0, Math.min(months, instData.history.length)) || [];

  return {
    institution_name: instData.institution_name,
    current: {
      date: instData.date,
      rating: instData.rating,
      score: instData.finalScore,
    },
    history: history.map((h: any) => ({
      date: h.date,
      rating: h.rating,
      score: h.finalScore,
    })),
    trend_analysis: history.length > 0 ? {
      oldest_score: history[history.length - 1].finalScore,
      newest_score: instData.finalScore,
      change: instData.finalScore - history[history.length - 1].finalScore,
      rating_stability: history.filter((h: any, idx: number) =>
        idx === 0 || h.rating === history[idx - 1].rating
      ).length === history.length,
    } : null,
  };
}

export function getComponentAnalysis(params: {
  institution_query: string;
  component_code: string;
  period?: string;
}) {
  const { institution_query, component_code, period } = params;

  const instData = getInstitutionData({ institution_query, period });
  if ('error' in instData) return instData;

  const component = instData.camel.find((c: any) => c.code === component_code);
  if (!component) {
    return { error: `Component ${component_code} not found` };
  }

  // Get all indicators for this component
  const indicators = instData.sankeyIndicators.filter((ind: any) => {
    const indComponent = getComponentForIndicator(ind.indicator_id);
    return indComponent === component_code;
  });

  // Calculate performance stats
  const scores = indicators.map((ind: any) => parseFloat(ind.indicator_score));
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
  const strongCount = scores.filter(s => s >= 66.68).length;
  const weakCount = scores.filter(s => s < 44.45).length;

  return {
    institution_name: instData.institution_name,
    period: instData.date,
    component_code,
    component_name: COMPONENT_INFO[component_code as keyof typeof COMPONENT_INFO].name,
    component_description: COMPONENT_INFO[component_code as keyof typeof COMPONENT_INFO].description,
    component_score: component.score,
    component_weight: COMPONENT_WEIGHTS[component_code as keyof typeof COMPONENT_WEIGHTS],
    performance_percentage: (component.score / COMPONENT_WEIGHTS[component_code as keyof typeof COMPONENT_WEIGHTS]) * 100,
    indicators: indicators.map((ind: any) => ({
      id: ind.indicator_id,
      name: ind.indicator_name,
      value: parseFloat(ind.indicator_value),
      score: parseFloat(ind.indicator_score),
      category: ind.indicator_category,
      performance: parseFloat(ind.indicator_score) >= 66.68 ? 'strong' :
                  parseFloat(ind.indicator_score) >= 44.45 ? 'adequate' : 'weak',
    })),
    summary: {
      total_indicators: indicators.length,
      average_score: avgScore,
      strong_indicators: strongCount,
      weak_indicators: weakCount,
    },
  };
}

export function getMethodologyInfo(params: { detail_level?: string } = {}) {
  const { detail_level = 'overview' } = params;

  const baseInfo = {
    rating_system: 'CAMEL',
    description: 'CAMEL is a supervisory rating system that evaluates financial institutions across 6 components',
    components: Object.entries(COMPONENT_INFO).map(([code, info]) => ({
      code,
      name: info.name,
      description: info.description,
      weight: COMPONENT_WEIGHTS[code as keyof typeof COMPONENT_WEIGHTS],
      weight_percentage: (COMPONENT_WEIGHTS[code as keyof typeof COMPONENT_WEIGHTS] * 100 / 100).toFixed(2) + '%',
    })),
    rating_scale: RATING_SCALE,
  };

  if (detail_level === 'detailed' || detail_level === 'formulas') {
    (baseInfo as any).calculation_process = {
      step1: 'Indicator values are collected from financial statements',
      step2: 'Each indicator is scored 0-100 using scorecard interpolation',
      step3: 'Indicators are weighted within their component',
      step4: 'Component scores are calculated as weighted averages',
      step5: 'Final CAMEL score is sum of all weighted component scores',
      step6: 'Final score is mapped to rating category (AAA to E)',
    };
  }

  if (detail_level === 'formulas') {
    (baseInfo as any).interpolation_formula = {
      formula: 'score = asociado_1 + ((value - inferior)/(superior - inferior)) × (asociado_2 - asociado_1)',
      description: 'Linear interpolation is used to score indicators based on their values falling within scorecard ranges',
      parameters: {
        value: 'Actual indicator value from financial data',
        inferior: 'Lower bound of the scorecard range',
        superior: 'Upper bound of the scorecard range',
        asociado_1: 'Score associated with the lower bound',
        asociado_2: 'Score associated with the upper bound',
      },
      example: {
        indicator: 'C2 (Morosidad/Default Rate)',
        scenario: 'Value of 0.063054 falls in range [0.05, 0.10]',
        calculation: 'score = 50 + ((0.063054 - 0.05)/(0.10 - 0.05)) × (0 - 50) = 36.95',
        interpretation: 'Higher default rate = lower score (inverse relationship)',
      },
    };
  }

  return baseInfo;
}

export function getWeakestStrongestIndicators(params: {
  institution_query: string;
  order: string;
  limit?: number;
  period?: string;
}) {
  const { institution_query, order, limit = 5, period } = params;

  const instData = getInstitutionData({ institution_query, period });
  if ('error' in instData) return instData;

  // Sort indicators by score
  const sorted = [...instData.sankeyIndicators].sort((a: any, b: any) => {
    const scoreA = parseFloat(a.indicator_score);
    const scoreB = parseFloat(b.indicator_score);
    return order === 'weakest' ? scoreA - scoreB : scoreB - scoreA;
  });

  const indicators = sorted.slice(0, limit).map((ind: any) => ({
    id: ind.indicator_id,
    name: ind.indicator_name,
    value: parseFloat(ind.indicator_value),
    score: parseFloat(ind.indicator_score),
    category: ind.indicator_category,
    component: getComponentForIndicator(ind.indicator_id),
    performance: parseFloat(ind.indicator_score) >= 66.68 ? 'strong' :
                parseFloat(ind.indicator_score) >= 44.45 ? 'adequate' : 'weak',
  }));

  return {
    institution_name: instData.institution_name,
    period: instData.date,
    order,
    indicators,
  };
}

export function getIndicatorHistory(params: {
  institution_query: string;
  indicator_id: string;
  months?: number;
}) {
  const { institution_query, indicator_id, months = 13 } = params;
  const data = realRatingData as Record<string, InstitutionData>;

  // Find institution
  const institutionId = findInstitutionId(institution_query);
  if (!institutionId) {
    return {
      error: `Institution "${institution_query}" not found`,
      available_institutions: Object.values(data).map(d => d.institution_name)
    };
  }

  const instData = data[institutionId];

  // Get current period indicator
  const currentIndicator = instData.sankeyIndicators.find(
    ind => ind.indicator_id.toUpperCase() === indicator_id.toUpperCase()
  );

  if (!currentIndicator) {
    return {
      error: `Indicator "${indicator_id}" not found for ${instData.institution_name}`,
      available_indicators: instData.sankeyIndicators.map(ind => ({
        id: ind.indicator_id,
        name: ind.indicator_name
      }))
    };
  }

  // Build history
  const history = instData.history
    ?.slice(0, Math.min(months, instData.history.length))
    .map((h: any) => {
      const indicator = h.sankeyIndicators?.find(
        (ind: any) => ind.indicator_id.toUpperCase() === indicator_id.toUpperCase()
      );
      return indicator ? {
        date: h.date,
        value: parseFloat(indicator.indicator_value),
        score: parseFloat(indicator.indicator_score),
      } : null;
    })
    .filter((item: any) => item !== null) || [];

  // Calculate trend
  const values = history.map((h: any) => h.value);
  const scores = history.map((h: any) => h.score);
  const trend = values.length > 1 ? {
    value_change: values[0] - values[values.length - 1],
    score_change: scores[0] - scores[scores.length - 1],
    value_trend: values[0] > values[values.length - 1] ? 'increasing' :
                  values[0] < values[values.length - 1] ? 'decreasing' : 'stable',
    score_trend: scores[0] > scores[scores.length - 1] ? 'improving' :
                  scores[0] < scores[scores.length - 1] ? 'declining' : 'stable',
  } : null;

  return {
    institution_name: instData.institution_name,
    indicator_id: currentIndicator.indicator_id,
    indicator_name: currentIndicator.indicator_name,
    indicator_category: currentIndicator.indicator_category,
    current: {
      date: instData.date,
      value: parseFloat(currentIndicator.indicator_value),
      score: parseFloat(currentIndicator.indicator_score),
    },
    history,
    trend,
    months_available: history.length,
  };
}

// Execute tool based on name
export function executeTool(toolName: string, toolInput: any) {
  try {
    switch (toolName) {
      case 'get_institution_data':
        return getInstitutionData(toolInput);
      case 'compare_institutions':
        return compareInstitutions(toolInput);
      case 'get_rating_history':
        return getRatingHistory(toolInput);
      case 'get_component_analysis':
        return getComponentAnalysis(toolInput);
      case 'get_methodology_info':
        return getMethodologyInfo(toolInput);
      case 'get_weakest_strongest_indicators':
        return getWeakestStrongestIndicators(toolInput);
      case 'get_indicator_history':
        return getIndicatorHistory(toolInput);
      default:
        return { error: `Unknown tool: ${toolName}` };
    }
  } catch (error) {
    console.error(`Error executing tool ${toolName}:`, error);
    return { error: `Failed to execute tool: ${error instanceof Error ? error.message : 'Unknown error'}` };
  }
}

// Tool definitions for Gemini (Vertex AI)
// Gemini uses a different format than Claude for function declarations
export const AGENT_TOOLS_GEMINI = [
  {
    name: 'get_institution_data',
    description: 'Get complete rating data for a financial institution including current rating, scores, all CAMEL components, and indicators. Use this when the user asks about a specific institution\'s rating or wants to understand why it has a certain rating.',
    parameters: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID to search for (e.g., "COOPROGRESO", "JARDIN AZUAYO", "ANDALUCIA", or institution ID like "COOP_1790451801001"). Partial matches work.',
        },
        period: {
          type: 'string',
          description: 'Optional period in YYYY-MM format (e.g., "2025-05", "2024-12"). If not provided, uses latest period.',
        },
      },
      required: ['institution_query'],
    },
  },
  {
    name: 'compare_institutions',
    description: 'Compare two financial institutions side-by-side, showing differences in ratings, scores, and all CAMEL components. Use this when the user asks to compare two institutions or wants to know how one institution stacks up against another.',
    parameters: {
      type: 'object',
      properties: {
        institution1_query: {
          type: 'string',
          description: 'First institution name or partial name (e.g., "JARDIN", "COOPROGRESO")',
        },
        institution2_query: {
          type: 'string',
          description: 'Second institution name or partial name',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest). Both institutions will be compared at this same period.',
        },
      },
      required: ['institution1_query', 'institution2_query'],
    },
  },
  {
    name: 'get_rating_history',
    description: 'Get historical rating trend for an institution over time, showing how the rating and score have changed month by month. Use this when the user asks about trends, changes over time, or how an institution has performed historically.',
    parameters: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        months: {
          type: 'number',
          description: 'Number of months to look back (default: 6, max: 13)',
        },
      },
      required: ['institution_query'],
    },
  },
  {
    name: 'get_component_analysis',
    description: 'Get detailed analysis of a specific CAMEL component including all its indicators, their scores, and weights. Use this when the user asks about a specific component (Capital, Liquidity, Profitability, etc.) or wants to understand what\'s driving a component\'s score.',
    parameters: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        component_code: {
          type: 'string',
          enum: ['C', 'EG', 'L', 'R', 'S', 'STI'],
          description: 'CAMEL component code: C (Capital/Asset Quality), EG (Efficiency & Earnings), L (Liquidity), R (Profitability), S (Solvency/Asset Quality), STI (Interest Rate Sensitivity)',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest)',
        },
      },
      required: ['institution_query', 'component_code'],
    },
  },
  {
    name: 'get_methodology_info',
    description: 'Get information about CAMEL rating methodology, including component weights, calculation formulas, and rating scale. Use this when the user asks how ratings are calculated, what the methodology is, or wants to understand the scoring system.',
    parameters: {
      type: 'object',
      properties: {
        detail_level: {
          type: 'string',
          enum: ['overview', 'detailed', 'formulas'],
          description: 'Level of detail: overview (basic explanation), detailed (with weights), formulas (including interpolation formulas and examples)',
        },
      },
      required: [],
    },
  },
  {
    name: 'get_weakest_strongest_indicators',
    description: 'Find the weakest or strongest performing indicators for an institution. Use this when the user asks what indicators are hurting/helping the score, what\'s dragging down/boosting the rating, or wants to see problem areas or strengths.',
    parameters: {
      type: 'object',
      properties: {
        institution_query: {
          type: 'string',
          description: 'Institution name or ID',
        },
        order: {
          type: 'string',
          enum: ['weakest', 'strongest'],
          description: 'Get weakest (lowest scoring) or strongest (highest scoring) indicators',
        },
        limit: {
          type: 'number',
          description: 'Number of indicators to return (default: 5)',
        },
        period: {
          type: 'string',
          description: 'Period in YYYY-MM format (default: latest)',
        },
      },
      required: ['institution_query', 'order'],
    },
  },
];
